import React, { useEffect, useMemo, useState } from 'react';
import { useGamingStore, Game } from '../../store/gamingStore';
import { extractDominantColor } from '../../lib/colorExtract';
import {
  ArrowLeft, Search, Play, Clock,
  Grid3X3, List, Gamepad2, Star, Download,
  Timer, ShoppingBag, Library, MoreVertical,
  Link2, RotateCcw, Trash2, ArrowUpCircle,
} from 'lucide-react';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../ui/alert-dialog';

type Filter = 'all' | 'installed' | 'recent';
type SortKey = 'recent' | 'added' | 'name-asc' | 'name-desc' | 'playtime';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'recent', label: 'Récemment joué' },
  { value: 'added', label: 'Récemment ajouté' },
  { value: 'name-asc', label: 'Nom (A → Z)' },
  { value: 'name-desc', label: 'Nom (Z → A)' },
  { value: 'playtime', label: 'Temps de jeu' },
];

const formatPlayTime = (mins: number | undefined) => {
  if (!mins || mins === 0) return 'Jamais joué';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h${m.toString().padStart(2, '0')}` : `${h}h`;
};

const timeAgo = (d: string) => {
  const m = Math.floor((Date.now() - +new Date(d)) / 60000);
  if (m < 1) return "à l'instant";
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h}h`;
  const days = Math.floor(h / 24);
  if (days < 30) return `il y a ${days}j`;
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

/* ═══════════════════════════════════════════════════════════════════════════
   LibraryCard — Game card with dynamic accent border color
   ═══════════════════════════════════════════════════════════════════════════ */

const LibraryCard: React.FC<{ game: Game; view: 'grid' | 'list' }> = ({ game, view }) => {
  const { openGameDetail, downloadGame, createGameShortcut, uninstallGame, gameUpdatesAvailable } = useGamingStore();
  const [accentColor, setAccentColor] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'uninstall' | 'reinstall' | null>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const isElectron = !!window.api;

  useEffect(() => {
    if (game.cover_image_url) {
      extractDominantColor(game.cover_image_url).then(setAccentColor);
    }
  }, [game.cover_image_url]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    if (showMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const borderStyle = accentColor
    ? { borderColor: accentColor.replace('rgb', 'rgba').replace(')', ', 0.45)') }
    : {};
  const shadowStyle = accentColor
    ? { boxShadow: `0 8px 24px ${accentColor.replace('rgb', 'rgba').replace(')', ', 0.15)')}` }
    : {};

  const confirmDialogs = (
    <>
      <AlertDialog open={confirmAction === 'uninstall'} onOpenChange={(open: boolean) => { if (!open) setConfirmAction(null); }}>
        <AlertDialogContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Désinstaller {game.title} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les fichiers du jeu seront supprimés de votre ordinateur. Vous pourrez le retélécharger à tout moment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={() => { setConfirmAction(null); uninstallGame(game.slug, game.title); }}
            >
              Désinstaller
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={confirmAction === 'reinstall'} onOpenChange={(open: boolean) => { if (!open) setConfirmAction(null); }}>
        <AlertDialogContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Réinstaller {game.title} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le jeu sera retéléchargé et les fichiers actuels seront remplacés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmAction(null); downloadGame(game.id, game.slug); }}>
              Réinstaller
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  /* ───── Grid view ───── */
  if (view === 'grid') {
    return (
      <>
      <div
        className="group relative flex flex-col rounded-xl overflow-hidden bg-bg-secondary border border-border-color transition-all duration-300 cursor-pointer hover:-translate-y-1"
        style={accentColor ? {
          '--card-accent': accentColor,
          '--card-accent-20': accentColor.replace('rgb', 'rgba').replace(')', ', 0.2)'),
        } as React.CSSProperties : {}}
        onClick={() => openGameDetail(game.slug)}
        onMouseEnter={(e) => {
          if (accentColor) {
            Object.assign(e.currentTarget.style, borderStyle, shadowStyle);
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '';
          e.currentTarget.style.boxShadow = '';
        }}
      >
        {/* Cover */}
        <div className="relative aspect-[2/3] overflow-hidden bg-bg-tertiary">
          {game.cover_image_url ? (
            <img
              src={game.cover_image_url}
              alt={game.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gaming-accent/20 to-bg-tertiary">
              <span className="text-2xl font-black text-gaming-accent/40">{game.title.charAt(0)}</span>
            </div>
          )}

          {/* Installed badge */}
          {game.installed && !game.is_suspended && !gameUpdatesAvailable[game.slug] && (
            <div className="absolute top-2 left-2 bg-emerald-500/90 text-black text-[10px] font-bold px-1.5 py-0.5 rounded">
              Installé
            </div>
          )}

          {/* Update available badge */}
          {game.installed && !game.is_suspended && gameUpdatesAvailable[game.slug] && (
            <div className="absolute top-2 left-2 bg-gaming-accent text-black text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1">
              <ArrowUpCircle className="w-3 h-3" />
              Mise à jour
            </div>
          )}

          {/* Suspended badge */}
          {!!game.is_suspended && (
            <div className="absolute top-2 left-2 bg-red-500/90 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
              Retiré
            </div>
          )}

          {/* Context menu */}
          {isElectron && game.installed && !game.is_suspended && (
            <div className="absolute top-2 right-2 z-20" ref={menuRef}>
              <button
                onClick={(e) => { e.stopPropagation(); setShowMenu((v) => !v); }}
                className="w-6 h-6 flex items-center justify-center rounded bg-black/60 text-white/70 hover:text-white hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-all"
              >
                <MoreVertical className="w-3.5 h-3.5" />
              </button>
              {showMenu && (
                <div className="absolute top-full right-0 mt-1 w-44 bg-bg-secondary border border-border-color rounded-lg shadow-xl py-1 overflow-hidden">
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowMenu(false); createGameShortcut(game.slug, { title: game.title }); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
                  >
                    <Link2 className="h-3.5 w-3.5" />
                    Créer un raccourci
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowMenu(false); setConfirmAction('reinstall'); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Réinstaller
                  </button>
                  <div className="border-t border-border-color my-1" />
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowMenu(false); setConfirmAction('uninstall'); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Désinstaller
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Time played overlay */}
          {(game.play_time_minutes ?? 0) > 0 && (
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-2.5 pb-2 pt-6">
              <span className="flex items-center gap-1 text-[10px] text-white/80 font-medium">
                <Timer className="w-2.5 h-2.5" /> {formatPlayTime(game.play_time_minutes)}
              </span>
            </div>
          )}

          {/* Hover overlay with Play button */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <button
              onClick={(e) => { e.stopPropagation(); openGameDetail(game.slug); }}
              className="w-12 h-12 rounded-full flex items-center justify-center transition-transform duration-200 hover:scale-110"
              style={{ backgroundColor: accentColor || 'var(--gaming-accent)' }}
            >
              <Play className="w-5 h-5 text-black fill-black ml-0.5" />
            </button>
          </div>
        </div>

        {/* Info */}
        <div className="p-2.5 flex-1 flex flex-col">
          <h3 className="text-sm font-semibold text-text-primary truncate group-hover:text-white transition-colors">
            {game.title}
          </h3>
          <p className="text-[11px] text-text-muted mt-0.5 truncate">{game.developer}</p>
          {game.last_played_at && (
            <p className="text-[10px] text-text-muted mt-1 flex items-center gap-1">
              <Clock className="w-2.5 h-2.5" /> {timeAgo(game.last_played_at)}
            </p>
          )}
        </div>
      </div>
      {confirmDialogs}
      </>
    );
  }

  /* ───── List view ───── */
  return (
    <>
    <div
      className="group flex items-center gap-4 p-3 rounded-xl bg-bg-secondary border border-border-color transition-all duration-300 cursor-pointer"
      onClick={() => openGameDetail(game.slug)}
      onMouseEnter={(e) => {
        if (accentColor) {
          Object.assign(e.currentTarget.style, borderStyle, shadowStyle);
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      {/* Thumbnail */}
      <div className="w-12 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-bg-tertiary">
        {game.cover_image_url ? (
          <img src={game.cover_image_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gaming-accent/20 to-bg-tertiary">
            <Gamepad2 className="w-4 h-4 text-gaming-accent/40" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-text-primary truncate group-hover:text-white transition-colors">
            {game.title}
          </h3>
          {!!game.is_suspended && (
            <span className="text-[10px] font-bold text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded shrink-0">Retiré</span>
          )}
          {!game.is_suspended && gameUpdatesAvailable[game.slug] && (
            <span className="text-[10px] font-bold text-black bg-gaming-accent px-1.5 py-0.5 rounded shrink-0 flex items-center gap-1">
              <ArrowUpCircle className="w-3 h-3" />
              Mise à jour
            </span>
          )}
        </div>
        <p className="text-[11px] text-text-muted truncate">{game.developer} {game.genre ? `· ${game.genre}` : ''}</p>
      </div>

      {/* Stats */}
      <div className="hidden sm:flex items-center gap-6 text-[11px] text-text-muted flex-shrink-0">
        <span className="flex items-center gap-1 w-20">
          <Timer className="w-3 h-3" /> {formatPlayTime(game.play_time_minutes)}
        </span>
        {game.last_played_at && (
          <span className="flex items-center gap-1 w-24 truncate">
            <Clock className="w-3 h-3" /> {timeAgo(game.last_played_at)}
          </span>
        )}
        {Number(game.rating_avg) > 0 && (
          <span className="flex items-center gap-0.5">
            <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
            {Number(game.rating_avg).toFixed(1)}
          </span>
        )}
      </div>

      {/* Play button */}
      <button
        onClick={(e) => { e.stopPropagation(); openGameDetail(game.slug); }}
        className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110"
        style={{ backgroundColor: accentColor || 'var(--gaming-accent)' }}
      >
        <Play className="w-4 h-4 text-black fill-black ml-0.5" />
      </button>

      {/* Context menu */}
      {isElectron && game.installed && !game.is_suspended && (
        <div className="relative flex-shrink-0" ref={view === 'list' ? menuRef : undefined}>
          <button
            onClick={(e) => { e.stopPropagation(); setShowMenu((v) => !v); }}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-text-muted hover:text-text-primary hover:bg-bg-tertiary opacity-0 group-hover:opacity-100 transition-all"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {showMenu && (
            <div className="absolute top-full right-0 mt-1 w-44 bg-bg-secondary border border-border-color rounded-lg shadow-xl z-50 py-1 overflow-hidden">
              <button
                onClick={(e) => { e.stopPropagation(); setShowMenu(false); createGameShortcut(game.slug, { title: game.title }); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
              >
                <Link2 className="h-3.5 w-3.5" />
                Créer un raccourci
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setShowMenu(false); setConfirmAction('reinstall'); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Réinstaller
              </button>
              <div className="border-t border-border-color my-1" />
              <button
                onClick={(e) => { e.stopPropagation(); setShowMenu(false); setConfirmAction('uninstall'); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Désinstaller
              </button>
            </div>
          )}
        </div>
      )}
    </div>
    {confirmDialogs}
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   GameLibrary — Main library page
   ═══════════════════════════════════════════════════════════════════════════ */

const GameLibrary: React.FC = () => {
  const {
    libraryGames, isLoadingLibrary,
    fetchLibrary, goBackToHub, setGamingView, openGameDetail,
  } = useGamingStore();

  const [filter, setFilter] = useState<Filter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('recent');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [heroAccent, setHeroAccent] = useState<string | null>(null);

  useEffect(() => { fetchLibrary(); }, [fetchLibrary]);

  // Hero = last played game
  const heroGame = useMemo(() => {
    return [...libraryGames]
      .filter(g => g.last_played_at)
      .sort((a, b) => +new Date(b.last_played_at!) - +new Date(a.last_played_at!))
      [0] ?? null;
  }, [libraryGames]);

  // Extract hero accent color
  useEffect(() => {
    if (heroGame?.cover_image_url) {
      extractDominantColor(heroGame.cover_image_url).then(setHeroAccent);
    }
  }, [heroGame?.cover_image_url]);

  // Filter + sort
  const filteredGames = useMemo(() => {
    let games = [...libraryGames];

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      games = games.filter(g =>
        g.title.toLowerCase().includes(q) ||
        g.developer?.toLowerCase().includes(q) ||
        g.genre?.toLowerCase().includes(q)
      );
    }

    // Filter
    if (filter === 'installed') games = games.filter(g => g.installed);
    if (filter === 'recent') games = games.filter(g => g.last_played_at);

    // Sort
    switch (sortKey) {
      case 'recent':
        games.sort((a, b) => {
          if (!a.last_played_at && !b.last_played_at) return 0;
          if (!a.last_played_at) return 1;
          if (!b.last_played_at) return -1;
          return +new Date(b.last_played_at) - +new Date(a.last_played_at);
        });
        break;
      case 'added':
        games.sort((a, b) => {
          if (!a.purchased_at && !b.purchased_at) return 0;
          if (!a.purchased_at) return 1;
          if (!b.purchased_at) return -1;
          return +new Date(b.purchased_at) - +new Date(a.purchased_at);
        });
        break;
      case 'name-asc':
        games.sort((a, b) => a.title.localeCompare(b.title, 'fr'));
        break;
      case 'name-desc':
        games.sort((a, b) => b.title.localeCompare(a.title, 'fr'));
        break;
      case 'playtime':
        games.sort((a, b) => (b.play_time_minutes ?? 0) - (a.play_time_minutes ?? 0));
        break;
    }

    return games;
  }, [libraryGames, search, filter, sortKey]);

  const FILTERS: { key: Filter; label: string; icon: React.ReactNode }[] = [
    { key: 'all', label: 'Tous', icon: <Library className="w-3.5 h-3.5" /> },
    { key: 'installed', label: 'Installés', icon: <Download className="w-3.5 h-3.5" /> },
    { key: 'recent', label: 'Récents', icon: <Clock className="w-3.5 h-3.5" /> },
  ];

  return (
    <div className="h-full overflow-y-auto gaming-scrollbar">
      <div className="space-y-6 pb-8">

        {/* ═══════════════════ HERO — Last played ═══════════════════ */}
        {heroGame && (
          <section
            className="relative h-56 lg:h-64 overflow-hidden cursor-pointer group"
            onClick={() => openGameDetail(heroGame.slug)}
          >
            {/* BG */}
            <div className="absolute inset-0">
              {heroGame.banner_image_url ? (
                <img
                  src={heroGame.banner_image_url}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                  alt=""
                />
              ) : heroGame.cover_image_url ? (
                <img
                  src={heroGame.cover_image_url}
                  className="w-full h-full object-cover blur-2xl scale-125 transition-transform duration-700 group-hover:scale-[1.3]"
                  alt=""
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-gaming-accent/20 via-purple-900/30 to-bg-primary" />
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-bg-primary via-bg-primary/85 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-bg-primary via-transparent to-bg-primary/40" />
            </div>

            {/* Bottom accent line */}
            <div
              className="absolute bottom-0 inset-x-0 h-px"
              style={{
                background: heroAccent
                  ? `linear-gradient(to right, transparent, ${heroAccent.replace('rgb', 'rgba').replace(')', ', 0.5)')}, transparent)`
                  : 'linear-gradient(to right, transparent, rgba(249,115,22,0.4), transparent)',
              }}
            />

            {/* Content */}
            <div className="relative z-10 h-full flex items-end px-8 pb-8">
              <div className="flex-1 max-w-lg">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] mb-2"
                  style={{ color: heroAccent || 'var(--gaming-accent)' }}
                >
                  <Play className="w-3 h-3 fill-current" /> Reprendre
                </span>
                <h1 className="text-3xl lg:text-4xl font-black text-white mb-1 leading-tight tracking-tight">
                  {heroGame.title}
                </h1>
                <div className="flex items-center gap-3 text-sm text-text-secondary mb-4">
                  {heroGame.last_played_at && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" /> {timeAgo(heroGame.last_played_at)}
                    </span>
                  )}
                  {(heroGame.play_time_minutes ?? 0) > 0 && (
                    <span className="flex items-center gap-1">
                      <Timer className="w-3.5 h-3.5" /> {formatPlayTime(heroGame.play_time_minutes)}
                    </span>
                  )}
                </div>
                <button
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-black font-bold text-sm transition-all hover:brightness-110"
                  style={{ backgroundColor: heroAccent || 'var(--gaming-accent)' }}
                  onClick={(e) => { e.stopPropagation(); openGameDetail(heroGame.slug); }}
                >
                  <Play className="w-4 h-4 fill-current" /> Reprendre la partie
                </button>
              </div>

              {heroGame.cover_image_url && (
                <div className="hidden lg:block w-28 aspect-[2/3] rounded-lg overflow-hidden shadow-2xl shadow-black/60 border border-white/10 ml-8 group-hover:scale-[1.03] transition-transform duration-500">
                  <img src={heroGame.cover_image_url} className="w-full h-full object-cover" alt="" />
                </div>
              )}
            </div>
          </section>
        )}

        {/* Padded wrapper */}
        <div className="max-w-7xl mx-auto px-6 space-y-5">

          {/* ═══════════════════ HEADER + CONTROLS ═══════════════════ */}
          <div className="space-y-4">
            {/* Title row */}
            <div className="flex items-center gap-4">
              <button
                onClick={goBackToHub}
                className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                <ArrowLeft className="h-4 w-4" /> Hub
              </button>
              <h1 className="text-2xl font-black text-text-primary">Mes jeux</h1>
              <span className="text-xs text-text-muted bg-bg-tertiary px-2 py-0.5 rounded-full font-medium">
                {libraryGames.length}
              </span>
            </div>

            {/* Controls row */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Filter tabs */}
              <div className="flex items-center gap-1.5 bg-bg-tertiary/50 rounded-lg p-1 border border-border-color">
                {FILTERS.map(f => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-medium transition-all duration-200 ${
                      filter === f.key
                        ? 'bg-gaming-accent/20 text-gaming-accent border border-gaming-accent/30'
                        : 'text-text-secondary hover:text-text-primary border border-transparent'
                    }`}
                  >
                    {f.icon} {f.label}
                  </button>
                ))}
              </div>

              {/* Search */}
              <div className="flex-1 min-w-[180px] max-w-xs relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-muted" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher dans ma bibliothèque..."
                  className="w-full bg-bg-tertiary border border-border-color rounded-lg pl-8 pr-3 py-1.5 text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gaming-accent/50 transition-colors"
                />
              </div>

              {/* Sort */}
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as SortKey)}
                className="bg-bg-tertiary border border-border-color rounded-lg px-3 py-1.5 text-xs text-text-secondary focus:outline-none focus:border-gaming-accent/50 cursor-pointer"
              >
                {SORT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>

              {/* View toggle */}
              <div className="flex items-center gap-0.5 bg-bg-tertiary rounded-lg p-0.5 border border-border-color">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-gaming-accent/20 text-gaming-accent' : 'text-text-muted hover:text-text-primary'}`}
                >
                  <Grid3X3 className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-gaming-accent/20 text-gaming-accent' : 'text-text-muted hover:text-text-primary'}`}
                >
                  <List className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>

          {/* ═══════════════════ GAME GRID / LIST ═══════════════════ */}
          {isLoadingLibrary ? (
            viewMode === 'grid' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i} className="aspect-[2/3] rounded-xl bg-bg-tertiary animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-[72px] rounded-xl bg-bg-tertiary animate-pulse" />
                ))}
              </div>
            )
          ) : filteredGames.length === 0 ? (
            /* ─── Empty state ─── */
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-20 h-20 rounded-2xl bg-bg-tertiary border border-border-color flex items-center justify-center mb-5">
                <Gamepad2 className="w-9 h-9 text-text-muted" />
              </div>
              {libraryGames.length === 0 ? (
                <>
                  <h3 className="text-lg font-bold text-text-primary mb-1">Ta bibliothèque est vide</h3>
                  <p className="text-sm text-text-muted mb-5 max-w-xs">
                    Explore le store pour découvrir des jeux et commence ta collection.
                  </p>
                  <button
                    onClick={() => setGamingView('store')}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gaming-accent text-black font-bold text-sm hover:bg-gaming-accent-hover transition-all"
                  >
                    <ShoppingBag className="w-4 h-4" /> Explorer le store
                  </button>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-bold text-text-primary mb-1">Aucun résultat</h3>
                  <p className="text-sm text-text-muted max-w-xs">
                    Aucun jeu ne correspond à ta recherche ou filtre.
                  </p>
                </>
              )}
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {filteredGames.map(game => (
                <LibraryCard key={game.id} game={game} view="grid" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredGames.map(game => (
                <LibraryCard key={game.id} game={game} view="list" />
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default GameLibrary;
